/**
 * Central export entitlement utility.
 * Server-side is source of truth — frontend MUST call API before export.
 *
 * Rules:
 *   Pro subscriber        → clean export
 *   Purchased product     → clean export
 *   Bundle includes slug  → clean export
 *   Admin                 → clean export
 *   Free / no entitlement → branded export ("Created with Lumera")
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Check export entitlement for a product. Must be called BEFORE export.
 * @param {string|null} productSlug - The product slug (null for free generation)
 * @returns {Promise<{clean: boolean, reason: string}>}
 */
export async function checkExportEntitlement(productSlug) {
  try {
    const params = productSlug ? { product_slug: productSlug } : {};
    const res = await axios.get(`${API}/content/export-entitlement`, { params });
    return res.data; // {clean: bool, reason: string}
  } catch {
    // If check fails (e.g. not logged in), default to branded
    return { clean: false, reason: 'free' };
  }
}

/**
 * Inject branding footer into a DOM element for html2canvas capture.
 * Returns a cleanup function that removes the branding.
 *
 * @param {HTMLElement} container - The element being captured
 * @returns {Function} cleanup - call this after html2canvas to remove branding
 */
export function injectExportBranding(container) {
  const brand = document.createElement('div');
  brand.setAttribute('data-lumera-branding', 'true');
  brand.style.cssText = [
    'position: absolute',
    'bottom: 0',
    'left: 0',
    'right: 0',
    'height: 28px',
    'background: linear-gradient(to right, #faf8f5, #f5f0eb)',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'gap: 6px',
    'border-top: 1px solid #e7e5e4',
    'z-index: 9999',
    'font-family: Georgia, "Times New Roman", serif',
    'pointer-events: none',
  ].join(';');

  const text = document.createElement('span');
  text.style.cssText = 'font-size: 10px; color: #a8a29e; letter-spacing: 0.5px;';
  text.textContent = 'Created with';

  const logo = document.createElement('span');
  logo.style.cssText = 'font-size: 11px; font-weight: 700; color: #78716c; letter-spacing: 1px;';
  logo.textContent = 'Lumera';

  brand.appendChild(text);
  brand.appendChild(logo);

  // Ensure container is positioned for absolute child
  const prevPosition = container.style.position;
  if (!prevPosition || prevPosition === 'static') {
    container.style.position = 'relative';
  }

  container.appendChild(brand);

  return () => {
    brand.remove();
    if (prevPosition !== undefined) {
      container.style.position = prevPosition;
    }
  };
}
